import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Linkedin } from "lucide-react";
import { COMPANY } from "@/data/content";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-obsidian text-white/70 grain relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-12">
          <div>
            <div className="inline-block bg-white rounded-2xl px-6 py-4 mb-6">
              <img src={COMPANY.logo} alt="Invesmo" className="h-14 w-auto" />
            </div>
            <p className="text-sm leading-relaxed">
              Debt capital, investment and development advisory. Based in Camberwell, servicing clients across Australia.
            </p>
            <div className="flex items-center gap-3 mt-6" data-testid="footer-social">
              <a
                href="https://www.linkedin.com/in/anu-prakash-a552ba5a/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-social-linkedin"
                aria-label="Invesmo on LinkedIn"
                className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-all duration-300"
              >
                <Linkedin size={17} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-[0.2em] mb-5">Explore</h4>
            <ul className="space-y-3 text-sm">
              <li><Link data-testid="footer-link-services" to="/services" className="hover:text-gold transition-colors">Services</Link></li>
              <li><Link data-testid="footer-link-who-we-help" to="/who-we-help" className="hover:text-gold transition-colors">Who We Help</Link></li>
              <li><Link data-testid="footer-link-about" to="/about" className="hover:text-gold transition-colors">About Anu Prakash</Link></li>
              <li><Link data-testid="footer-link-calculators" to="/calculators" className="hover:text-gold transition-colors">Calculators</Link></li>
              <li><Link data-testid="footer-link-resources" to="/resources" className="hover:text-gold transition-colors">Resources & Guides</Link></li>
              <li><Link data-testid="footer-link-book" to="/book" className="hover:text-gold transition-colors">Book a Meeting</Link></li>
              <li><Link data-testid="footer-link-callback" to="/callback" className="hover:text-gold transition-colors">Request a Callback</Link></li>
              <li><Link data-testid="footer-link-privacy" to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-[0.2em] mb-5">Specialist Finance</h4>
            <ul className="space-y-3 text-sm">
              <li><Link data-testid="footer-link-commercial-mortgages" to="/commercial-mortgages" className="hover:text-gold transition-colors">Commercial Mortgages</Link></li>
              <li><Link data-testid="footer-link-medical-practice-finance" to="/medical-practice-finance" className="hover:text-gold transition-colors">Doctors & Medical</Link></li>
              <li><Link data-testid="footer-link-finance-for-accountants" to="/finance-for-accountants" className="hover:text-gold transition-colors">Accountants & CPAs</Link></li>
              <li><Link data-testid="footer-link-dental-practice-finance" to="/dental-practice-finance" className="hover:text-gold transition-colors">Dentists</Link></li>
              <li><Link data-testid="footer-link-professional-practice-finance" to="/professional-practice-finance" className="hover:text-gold transition-colors">Lawyers, Vets & Allied Health</Link></li>
              <li><Link data-testid="footer-link-property-development-finance" to="/property-development-finance" className="hover:text-gold transition-colors">Property Developers</Link></li>
              <li><Link data-testid="footer-link-business-acquisition-finance" to="/business-acquisition-finance" className="hover:text-gold transition-colors">Business Acquisitions</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-[0.2em] mb-5">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone size={15} className="mt-0.5 text-gold shrink-0" />
                <a data-testid="footer-phone" href={`tel:${COMPANY.phone}`} className="hover:text-gold transition-colors">{COMPANY.phoneDisplay}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={15} className="mt-0.5 text-gold shrink-0" />
                <a data-testid="footer-email" href={`mailto:${COMPANY.email}`} className="hover:text-gold transition-colors">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={15} className="mt-0.5 text-gold shrink-0" />
                <span data-testid="footer-address">{COMPANY.address}</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-[0.2em] mb-5">Regulatory</h4>
            <ul className="space-y-2 text-xs leading-relaxed">
              <li>ABN {COMPANY.abn}</li>
              <li>Credit Representative Number {COMPANY.crn}</li>
              <li>{COMPANY.aggregator} · ACL {COMPANY.acl}</li>
              <li>AFCA Member {COMPANY.afcaMember}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 space-y-4 text-[11px] leading-relaxed text-white/45">
          <p data-testid="footer-credit-disclosure">
            Invesmo Pty Ltd (ABN {COMPANY.abn}), Credit Representative Number {COMPANY.crn}, is an authorised credit
            representative of {COMPANY.aggregator}, Australian Credit Licence {COMPANY.acl}. Invesmo Pty Ltd is a member of the
            Australian Financial Complaints Authority (AFCA), membership number {COMPANY.afcaMember}. {COMPANY.aggregator} AFCA
            membership number {COMPANY.afca}.
          </p>
          <p data-testid="footer-advice-disclaimer">
            The information on this website is general in nature and does not constitute financial, legal, taxation or credit
            advice. It does not take into account your objectives, financial situation or needs. Before acting on any
            information, you should consider its appropriateness to your circumstances and seek professional advice. All loan
            applications are subject to lender credit assessment, eligibility criteria, terms, conditions, fees and charges.
            *LMI waivers and preferential pricing are subject to lender policy and eligibility.
          </p>
          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-2">
            <span>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</span>
            <span>{COMPANY.domain}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
